import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Stack,
  Chip,
  Divider,
} from "@mui/material";

/**
 * Modal para intercambiar ingredientes de la misma categoría.
 *
 * Props:
 * - open: boolean, controla visibilidad del modal
 * - onClose: fn, cierra el modal
 * - ingredientes: array de todos los ingredientes disponibles
 * - swapTarget: objeto, ingrediente original con la cantidad/equivalentes a usar y texto de categoría
 * - onSelect: fn, recibe el ingrediente seleccionado
 */
export default function SwapIngredientModal({
  open,
  onClose,
  ingredientes = [],
  swapTarget,
  onSelect,
}) {
  // factor de equivalentes a aplicación
  const factor = swapTarget?.equivalentes || 1;
  const categoryId = swapTarget?.categoria;
  const categoryText = swapTarget?.categoriaTexto || "";

  const filtered = useMemo(
    () =>
      Array.isArray(ingredientes)
        ? ingredientes.filter((i) => i.categoria === categoryId)
        : [],
    [ingredientes, categoryId]
  );

  const handleSelect = (item) => {
    onSelect(item);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 25px 60px rgba(15,23,42,0.18)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack spacing={1}>
          <Typography variant="h6" fontWeight={700}>
            Intercambiar ingrediente
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Explora alternativas dentro de la misma categoría para mantener tu
            plan equilibrado.
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {swapTarget && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              background: "#fff",
              borderBottom: "1px solid",
              borderColor: "divider",
              px: 3,
              py: 2,
            }}
          >
            <Stack spacing={1}>
              <Chip
                label={categoryText || "Categoría"}
                size="small"
                sx={{ alignSelf: "flex-start", textTransform: "capitalize" }}
              />
              <Typography variant="body2" color="text.secondary">
                Puedes reemplazar{" "}
                <b>
                  {swapTarget.cantidad} {swapTarget.unidad}
                </b>{" "}
                de <b>{swapTarget.nombre}</b> por cualquiera de las siguientes
                opciones.
              </Typography>
            </Stack>
          </Box>
        )}

        {filtered.length > 0 ? (
          <List sx={{ py: 1 }}>
            {filtered.map((ing, idx) => {
              const qty = (ing.cantidad || 0) * factor;
              return (
                <React.Fragment key={ing._id || idx}>
                  <ListItemButton onClick={() => handleSelect(ing)}>
                    <ListItemText
                      primary={`${qty.toFixed(2)} ${ing.unidad || ""} de ${
                        ing.nombre
                      }`}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItemButton>
                  {idx < filtered.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            No hay otros ingredientes disponibles en esta categoría.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SwapIngredientModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ingredientes: PropTypes.array.isRequired,
  swapTarget: PropTypes.shape({
    nombre: PropTypes.string,
    cantidad: PropTypes.number,
    unidad: PropTypes.string,
    equivalentes: PropTypes.number,
    categoria: PropTypes.string,
    categoriaTexto: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
};
